interface HandoffIdentity {
  userId: string;
  accountId: string;
  sessionJWT: string;
}

const HANDOFF_TIMEOUT_MS = 60000;
const RECEIVE_TIMEOUT_MS = 30000;
const pending = new Map<string, Promise<string>>();

export function openAuthHandoff(target: string, getIdentity: () => HandoffIdentity | null): Window | null {
  const identity = getIdentity();
  if (!identity?.sessionJWT) return null;
  const url = new URL(target, window.location.origin);
  if (url.searchParams.get("user_id") !== identity.userId
      || url.searchParams.get("account_id") !== identity.accountId) return null;
  const handoffId = crypto.randomUUID();
  url.searchParams.set("handoff_id", handoffId);
  let child: Window | null = null;
  const cleanup = () => {
    window.removeEventListener("message", receive);
    clearTimeout(timeout);
  };
  const receive = (event: MessageEvent) => {
    if (event.origin !== url.origin || event.source !== child
        || event.data?.type !== "RAVEN_AUTH_REQUEST" || event.data.handoffId !== handoffId) return;
    const current = getIdentity();
    if (current?.userId === identity.userId && current.accountId === identity.accountId && current.sessionJWT) {
      child?.postMessage({ type: "RAVEN_AUTH_RESPONSE", handoffId, session_jwt: current.sessionJWT }, url.origin);
    }
    cleanup();
  };
  const timeout = setTimeout(cleanup, HANDOFF_TIMEOUT_MS);
  window.addEventListener("message", receive);
  // The receiver needs its opener for the authenticated message exchange.
  child = window.open(url.toString(), "_blank");
  if (!child) cleanup();
  return child;
}

export function trustedHandoffOrigin(referrer: string | null): string | null {
  if (!referrer) return null;
  try {
    const origin = new URL(referrer).origin;
    const allowed = [
      window.location.origin,
      "https://nmms.startraven.com", "https://nmms.staging.startraven.com",
      "https://admin.startraven.com", "https://admin.staging.ravenapp.dev",
      process.env.REACT_APP_ADMIN_FRONTEND_URL,
      ...(process.env.REACT_APP_AUTH_HANDOFF_ORIGINS || "").split(","),
    ].filter(Boolean).map((value) => new URL(value!.trim()).origin);
    return allowed.includes(origin) ? origin : null;
  } catch {
    return null;
  }
}

export function receiveAuthHandoff(handoffId: string | null, sourceOrigin: string | null): Promise<string> {
  if (!handoffId || !/^[0-9a-f-]{36}$/i.test(handoffId) || !sourceOrigin || !window.opener) {
    return Promise.reject(new Error("Please sign in to ESS directly."));
  }
  const key = `${sourceOrigin}:${handoffId}`;
  const existing = pending.get(key);
  if (existing) return existing;
  // StrictMode's effect replay shares the in-flight exchange, so its first
  // cleanup cannot consume a response needed by the live effect.
  const promise = new Promise<string>((resolve, reject) => {
    const opener = window.opener;
    const cleanup = () => {
      window.removeEventListener("message", receive);
      clearTimeout(timeout);
    };
    const receive = (event: MessageEvent) => {
      if (event.origin !== sourceOrigin || event.source !== opener
          || event.data?.type !== "RAVEN_AUTH_RESPONSE" || event.data.handoffId !== handoffId
          || typeof event.data.session_jwt !== "string" || !event.data.session_jwt.trim()) return;
      cleanup();
      resolve(event.data.session_jwt);
    };
    const timeout = setTimeout(() => { cleanup(); reject(new Error("Sign-in handoff expired. Please sign in directly.")); }, RECEIVE_TIMEOUT_MS);
    window.addEventListener("message", receive);
    opener.postMessage({ type: "RAVEN_AUTH_REQUEST", handoffId }, sourceOrigin);
  });
  pending.set(key, promise);
  void promise.then(() => pending.delete(key), () => pending.delete(key));
  return promise;
}
